import { useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  FileText, Download, Calendar, TrendingUp, Users, DollarSign,
  Briefcase, Filter, Printer, Mail, ChevronDown, BarChart3, PieChart,
  ArrowUpRight, Clock
} from "lucide-react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.05 } },
};

const monthlyGrowth = [
  { month: "Oct", users: 1820, providers: 245, bookings: 620, revenue: 24500 },
  { month: "Nov", users: 2010, providers: 268, bookings: 740, revenue: 29800 },
  { month: "Dec", users: 2250, providers: 289, bookings: 890, revenue: 38200 },
  { month: "Jan", users: 2480, providers: 305, bookings: 960, revenue: 41500 },
  { month: "Feb", users: 2650, providers: 325, bookings: 1080, revenue: 47200 },
  { month: "Mar", users: 2847, providers: 342, bookings: 1204, revenue: 56400 },
];

const topServicesByRevenue = [
  { service: "Hair Coloring", bookings: 186, revenue: 15810, avgPrice: "$85" },
  { service: "Deep Tissue Massage", bookings: 142, revenue: 17040, avgPrice: "$120" },
  { service: "Custom Tattoo", bookings: 68, revenue: 17000, avgPrice: "$250" },
  { service: "Gel Manicure", bookings: 210, revenue: 11550, avgPrice: "$55" },
  { service: "Haircut & Beard", bookings: 280, revenue: 12600, avgPrice: "$45" },
  { service: "Facial Treatment", bookings: 95, revenue: 9500, avgPrice: "$100" },
];

const regionData = [
  { region: "New York, NY", providers: 48, bookings: 340, revenue: "$18,200" },
  { region: "Los Angeles, CA", providers: 62, bookings: 280, revenue: "$14,800" },
  { region: "Chicago, IL", providers: 35, bookings: 195, revenue: "$9,600" },
  { region: "Austin, TX", providers: 28, bookings: 168, revenue: "$8,400" },
  { region: "Miami, FL", providers: 41, bookings: 220, revenue: "$11,200" },
];

const savedReports = [
  { name: "Monthly Revenue Summary", type: "Financial", lastRun: "Mar 30, 2026", schedule: "Monthly", format: "PDF" },
  { name: "Provider Performance Q1", type: "Performance", lastRun: "Mar 28, 2026", schedule: "Quarterly", format: "Excel" },
  { name: "Customer Acquisition Report", type: "Growth", lastRun: "Mar 25, 2026", schedule: "Weekly", format: "PDF" },
  { name: "Booking Trends Analysis", type: "Operations", lastRun: "Mar 30, 2026", schedule: "Daily", format: "CSV" },
  { name: "Commission Earnings Report", type: "Financial", lastRun: "Mar 28, 2026", schedule: "Monthly", format: "Excel" },
];

const kpiSummary = [
  { label: "Total Revenue (Q1)", value: "$145,100", change: "+34.2%", icon: DollarSign },
  { label: "New Users (Q1)", value: "1,027", change: "+28.6%", icon: Users },
  { label: "Bookings (Q1)", value: "3,244", change: "+41.3%", icon: Calendar },
  { label: "Active Providers", value: "342", change: "+15.8%", icon: Briefcase },
];

type ReportTab = "overview" | "generated" | "regional";

const typeColor = (type: string) => {
  switch (type) {
    case "Financial": return "bg-primary/10 text-primary border-primary/20";
    case "Performance": return "bg-secondary/10 text-secondary-foreground border-secondary/20";
    case "Growth": return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Operations": return "bg-accent/10 text-accent-foreground border-accent/20";
    default: return "bg-muted text-muted-foreground";
  }
};

const AdminReports = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>("overview");
  const [period, setPeriod] = useState<"6m" | "3m" | "1m">("6m");

  const chartData = period === "1m" ? monthlyGrowth.slice(-1) : period === "3m" ? monthlyGrowth.slice(-3) : monthlyGrowth;

  const tabs: { key: ReportTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "generated", label: "Saved Reports" },
    { key: "regional", label: "Regional" },
  ];

  return (
    <motion.div key="reports" {...fadeIn}>
      {/* Sub-tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex rounded-lg border border-border overflow-hidden">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {(["6m", "3m", "1m"] as const).map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${period === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                {p}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
            <Download className="h-3 w-3" /> Export
          </Button>
        </div>
      </div>

      {activeTab === "overview" && (
        <div>
          {/* KPI cards */}
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" variants={stagger} initial="initial" animate="animate">
            {kpiSummary.map(({ label, value, change, icon: Icon }) => (
              <motion.div key={label} variants={fadeIn} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
                  <span className="flex items-center gap-0.5 text-xs font-medium text-emerald-600">
                    <ArrowUpRight className="h-3 w-3" />{change}
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Growth chart */}
          <div className="rounded-xl border border-border bg-background p-4 mb-4">
            <h4 className="font-semibold text-foreground mb-4">Platform Growth</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(0, 0%, 100%)", border: "1px solid hsl(44, 30%, 85%)", borderRadius: "8px", fontSize: "12px" }} />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Line type="monotone" dataKey="users" name="Users" stroke="hsl(357, 25%, 63%)" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="bookings" name="Bookings" stroke="hsl(60, 13%, 57%)" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="providers" name="Providers" stroke="hsl(51, 44%, 71%)" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top services by revenue */}
          <div className="rounded-xl border border-border bg-background p-4">
            <h4 className="font-semibold text-foreground mb-3">Top Services by Revenue</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 font-medium text-muted-foreground">Service</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Bookings</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Revenue</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground hidden sm:table-cell">Avg. Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {topServicesByRevenue.map(s => (
                    <tr key={s.service} className="hover:bg-muted/20 transition-colors">
                      <td className="py-2.5 font-medium text-foreground">{s.service}</td>
                      <td className="py-2.5 text-right text-muted-foreground">{s.bookings}</td>
                      <td className="py-2.5 text-right font-medium text-foreground">${s.revenue.toLocaleString()}</td>
                      <td className="py-2.5 text-right text-muted-foreground hidden sm:table-cell">{s.avgPrice}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === "generated" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-foreground">Saved & Scheduled Reports</h4>
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
              <FileText className="h-3 w-3" /> Create Report
            </Button>
          </div>
          <div className="space-y-3">
            {savedReports.map((r, i) => (
              <motion.div key={i} variants={fadeIn} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-lg bg-muted/50">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{r.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${typeColor(r.type)}`}>{r.type}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{r.schedule}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Last generated: {r.lastRun}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{r.format}</Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7"><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7"><Printer className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7"><Mail className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "regional" && (
        <div>
          {/* Regional revenue chart */}
          <div className="rounded-xl border border-border bg-background p-4 mb-4">
            <h4 className="font-semibold text-foreground mb-4">Revenue by Region</h4>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={regionData} layout="vertical" barSize={16}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
                  <YAxis type="category" dataKey="region" tick={{ fontSize: 11 }} stroke="hsl(60, 13%, 40%)" width={110} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(0, 0%, 100%)", border: "1px solid hsl(44, 30%, 85%)", borderRadius: "8px", fontSize: "12px" }} />
                  <Bar dataKey="bookings" name="Bookings" fill="hsl(357, 25%, 63%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Regional table */}
          <div className="rounded-xl border border-border bg-background p-4">
            <h4 className="font-semibold text-foreground mb-3">Regional Performance</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 font-medium text-muted-foreground">Region</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Providers</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Bookings</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {regionData.map(r => (
                    <tr key={r.region} className="hover:bg-muted/20 transition-colors">
                      <td className="py-2.5 font-medium text-foreground">{r.region}</td>
                      <td className="py-2.5 text-right text-muted-foreground">{r.providers}</td>
                      <td className="py-2.5 text-right text-muted-foreground">{r.bookings}</td>
                      <td className="py-2.5 text-right font-medium text-foreground">{r.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminReports;
